import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getAgency, getAgencyUsers, inviteUserToAgency, deleteUser, getAgencyClients, getProposals, uploadAgencyLogo, updateUserProfile, updateAgencyBranding, createClient, API_BASE } from "../lib/api";
import DirectMessageComposeModal from "../components/DirectMessageComposeModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Plus, Trash2, Users, Loader2, Copy, CheckCircle2, Briefcase, DollarSign, FileSignature, Image as ImageIcon, Upload, Pencil, Phone, UserPlus, Download } from "lucide-react";
import { toast } from "sonner";

const lifecycleLabels = {
  onboarding: { label: "Onboarding", className: "bg-blue-100 text-blue-800" },
  active: { label: "Active", className: "bg-emerald-100 text-emerald-800" },
  monthly_active: { label: "Monthly Active", className: "bg-emerald-100 text-emerald-800" },
  pending_renewal: { label: "Pending Renewal", className: "bg-amber-100 text-amber-800" },
  archived: { label: "Archived", className: "bg-slate-100 text-slate-700" },
};
const formatMoney = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AgencyDetailPage() {
  const { agencyId } = useParams();
  const { user: currentUser } = useAuth();
  const [agency, setAgency] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // Add-client dialog (super_admin only)
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    company: "", website: "", industry: "", name: "", email: "", phone: "", rep_id: "",
  });
  // Clients list — "Live" (has any non-archived project), "Archived" (all
  // projects archived/cancelled/completed), or "All". Live is the default
  // because reps almost always want to see who's currently paying them.
  const [clientFilter, setClientFilter] = useState("live");

  const isProjectLive = (p) => {
    const stage = (p?.lifecycle_stage || "").toLowerCase();
    const status = (p?.status || "").toLowerCase();
    if (stage === "archived") return false;
    if (["completed", "cancelled", "canceled", "archived"].includes(status)) return false;
    return true;
  };

  const filteredClients = clients.filter((c) => {
    const projects = c.projects && c.projects.length ? c.projects : (c.primary_project ? [c.primary_project] : []);
    const hasLive = projects.some(isProjectLive);
    if (clientFilter === "live") return hasLive || projects.length === 0;   // clients with no orders yet stay visible under Live
    if (clientFilter === "archived") return !hasLive && projects.length > 0;
    return true; // all
  });

  const liveCount = clients.filter((c) => {
    const projects = c.projects && c.projects.length ? c.projects : (c.primary_project ? [c.primary_project] : []);
    return projects.length === 0 || projects.some(isProjectLive);
  }).length;
  const archivedCount = clients.length - liveCount;

  const exportClientsCsv = () => {
    // Downloadable CSV of the CURRENT filtered view. Columns match what the
    // user asked for so they can share it with someone outside the portal.
    if (!clients.length) { toast.error("No clients to export"); return; }
    const header = [
      "Agency Name", "Client Company", "Client Contact", "Client Email", "Rep Name", "Rep Email",
      "Products", "Start Date", "End Date", "Monthly Amount", "Contract Value", "Status",
    ];
    const rows = [];
    const csvSafe = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    filteredClients.forEach((c) => {
      const projects = c.projects && c.projects.length ? c.projects : (c.primary_project ? [c.primary_project] : []);
      if (projects.length === 0) {
        rows.push([
          agency?.name, c.company || c.name, c.name || "", c.email || "", c.rep_name || "", c.rep_email || "",
          "", "", "", "", "", "no orders",
        ]);
        return;
      }
      projects.forEach((p) => {
        const products = (p.sub_product_names && p.sub_product_names.length ? p.sub_product_names : (p.product_types || []).map((t) => t.replace(/_/g, " "))).join(" · ");
        rows.push([
          agency?.name, c.company || c.name, c.name || "", c.email || "", c.rep_name || "", c.rep_email || "",
          products,
          p.start_date || "", p.end_date || (p.auto_renew ? "Month-to-Month" : ""),
          p.monthly_amount != null ? p.monthly_amount : "",
          p.contract_value != null ? p.contract_value : "",
          p.lifecycle_stage || p.status || "",
        ]);
      });
    });
    const csv = [header, ...rows].map((r) => r.map(csvSafe).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    const label = clientFilter === "live" ? "Live" : clientFilter === "archived" ? "Archived" : "All";
    a.download = `${(agency?.name || "Agency").replace(/[^A-Za-z0-9]+/g, "_")}_${label}_Clients_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };

  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "rep",
  });

  const fetchData = async () => {
    // allSettled instead of all — one 403 (e.g. proposals or clients for a
    // rep who can only see their subset) shouldn't zero-out the whole page.
    try {
      const [agencyRes, usersRes, clientsRes, proposalsRes] = await Promise.allSettled([
        getAgency(agencyId),
        getAgencyUsers(agencyId),
        getAgencyClients(agencyId),
        getProposals({ agency_id: agencyId }),
      ]);
      if (agencyRes.status === "fulfilled") setAgency(agencyRes.value.data);
      if (usersRes.status === "fulfilled") setUsers(usersRes.value.data || []);
      if (clientsRes.status === "fulfilled") setClients(clientsRes.value.data || []);
      if (proposalsRes.status === "fulfilled") setProposals(proposalsRes.value.data || []);
      // If the primary agency call failed (e.g. cross-tenant 403), surface a
      // clean 'not found' state — otherwise render the page with whatever we got.
      if (agencyRes.status === "rejected") {
        console.error("[agency-detail] agency load failed:", agencyRes.reason);
      }
    } catch (error) {
      console.error("Failed to fetch agency data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [agencyId]);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setInviteResult(null);
    try {
      const { data } = await inviteUserToAgency(agencyId, inviteForm);
      setInviteResult(data);
      setInviteForm({ email: "", name: "", role: "rep" });
      fetchData();
    } catch (error) {
      console.error("Failed to invite user:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await deleteUser(selectedUser.id);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      super_admin: "role-super_admin",
      manager: "role-manager",
      rep: "role-rep",
    };
    return classes[role] || "role-rep";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isSuperAdmin = currentUser?.role === "super_admin";
  const agencyReps = users.filter((u) => u.role === "rep" || u.role === "manager");

  const handleAddClient = async () => {
    const company = (newClient.company || "").trim();
    const email = (newClient.email || "").trim();
    if (!company || !email) {
      toast.error("Company name and email are required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email (e.g. name@company.com)");
      return;
    }
    setAddingClient(true);
    try {
      const payload = {
        company, email,
        website: newClient.website || null,
        industry: newClient.industry || null,
        name: newClient.name || null,
        phone: newClient.phone || null,
        agency_id: agencyId,
        rep_id: newClient.rep_id || null,
      };
      const { data } = await createClient(payload);
      setClients((prev) => [...prev, data]);
      toast.success(`"${data.company}" added to ${agency.name}`);
      setAddClientOpen(false);
      setNewClient({ company: "", website: "", industry: "", name: "", email: "", phone: "", rep_id: "" });
    } catch (err) {
      // Normalize FastAPI 422 array-of-objects into a readable string
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((e) => `${(e?.loc || []).slice(-1)[0] || "field"}: ${e?.msg || e}`).join("; ")
        : (err?.message || "Failed to add client");
      toast.error(msg);
    } finally {
      setAddingClient(false);
    }
  };

  // Mirror of the Users page badge — pre-existing users with activity show "Active",
  // pre-existing without activity show "Unknown", new invitees never logged in show
  // red "Never". Real timestamps get relative time.
  const formatLastLogin = (iso, preExisting = false, preTrackingActive = false) => {
    if (!iso) {
      if (preExisting && preTrackingActive) return { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
      if (preExisting) return { label: "Unknown", tone: "bg-zinc-100 text-zinc-600 border-zinc-200 italic" };
      return { label: "Never", tone: "bg-red-50 text-red-700 border-red-200" };
    }
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return { label: "Never", tone: "bg-red-50 text-red-700 border-red-200" };
    const diffMs = Date.now() - then;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    let label;
    let tone;
    if (diffMin < 5) label = "Just now";
    else if (diffMin < 60) label = `${diffMin}m ago`;
    else if (diffHr < 24) label = `${diffHr}h ago`;
    else if (diffDay === 1) label = "Yesterday";
    else if (diffDay < 30) label = `${diffDay}d ago`;
    else if (diffDay < 365) label = `${Math.floor(diffDay / 30)}mo ago`;
    else label = `${Math.floor(diffDay / 365)}y ago`;
    if (diffDay <= 7) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
    else if (diffDay <= 30) tone = "bg-zinc-100 text-zinc-700 border-zinc-200";
    else tone = "bg-amber-50 text-amber-800 border-amber-200";
    return { label, tone };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-muted-foreground">Agency not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">{agency.name}</h1>
          <p className="text-muted-foreground mt-1">
            {agency.user_count} users • Created {formatDate(agency.created_at)}
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <DirectMessageComposeModal
              agencyId={agency.id}
              agencyName={agency.name}
            />
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setInviteResult(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-button">
              <Plus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite User to {agency.name}</DialogTitle>
              <DialogDescription>
                Send an invite link so they can set up their password and join this agency.
              </DialogDescription>
            </DialogHeader>
            {inviteResult ? (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    User invited successfully! Share these credentials:
                  </AlertDescription>
                </Alert>
                <div className="space-y-3 p-4 bg-secondary rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="font-medium">{inviteResult.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Temporary Password</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-background rounded border text-sm">
                        {inviteResult.temp_password}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(inviteResult.temp_password)}
                        data-testid="copy-password-button"
                      >
                        {copied ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                <Button onClick={() => {
                  setDialogOpen(false);
                  setInviteResult(null);
                }} className="w-full">
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={handleInviteUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Full name"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                  >
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="rep">Sales Rep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-invite-button">
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Inviting...
                    </>
                  ) : (
                    "Send Invitation"
                  )}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
          </div>
        )}
      </div>

      {/* Agency Branding (logo) — super_admin only */}
      {isSuperAdmin && <AgencyBrandingCard agency={agency} onChange={fetchData} />}

      {/* Users List */}
      <Card className="border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No users yet. Invite your first team member.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[110px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="table-row-hover">
                    <TableCell className="font-medium">
                      <Link
                        to={`/users/${user.id}`}
                        className="text-primary hover:underline"
                        data-testid={`team-rep-link-${user.id}`}
                      >
                        {user.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeClass(user.role)}>
                        {user.role === "manager" ? "Manager" : "Sales Rep"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.title || <span className="text-zinc-400 italic">—</span>}</TableCell>
                    <TableCell className="text-muted-foreground">{user.phone || <span className="text-zinc-400 italic">—</span>}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell data-testid={`agency-last-login-${user.id}`}>
                      {(() => {
                        const { label, tone } = formatLastLogin(user.last_login_at, user.login_tracking_pre_existing, user.pre_tracking_active);
                        let tooltipText;
                        if (user.last_login_at) {
                          tooltipText = new Date(user.last_login_at).toLocaleString();
                        } else if (user.pre_tracking_active) {
                          tooltipText = "This user has clients/orders/messages in the portal, so they clearly used it — exact date not yet recorded.";
                        } else if (user.login_tracking_pre_existing) {
                          tooltipText = "This user existed before login tracking was added and has no recorded activity — we can't tell if they've used the portal.";
                        } else {
                          tooltipText = "This user has never logged into the portal";
                        }
                        return (
                          <Badge variant="outline" className={`text-xs font-medium ${tone}`} title={tooltipText}>
                            {label}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <RepProfileEditor rep={user} onSaved={fetchData} />
                        {isSuperAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pending Proposals for this agency */}
      <Card className="border" data-testid="agency-proposals-card">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            Pending Proposals
            {(() => {
              const pending = proposals.filter((p) => ["requested", "created", "presented"].includes(p.status));
              return pending.length > 0 ? <Badge variant="outline" className="ml-1">{pending.length}</Badge> : null;
            })()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const pending = proposals
              .filter((p) => ["requested", "created", "presented"].includes(p.status))
              .sort((a, b) => (a.client_name || "").toLowerCase().localeCompare((b.client_name || "").toLowerCase()));
            if (pending.length === 0) {
              return (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No pending proposals for this agency.
                </p>
              );
            }
            const statusMeta = {
              requested: { label: "Requested", className: "bg-amber-100 text-amber-800" },
              created: { label: "Created", className: "bg-blue-100 text-blue-800" },
              presented: { label: "Presented", className: "bg-indigo-100 text-indigo-800" },
            };
            return (
              <div className="rounded-md border divide-y">
                {pending.map((p) => (
                  <div
                    key={p.id}
                    className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm"
                    data-testid={`agency-proposal-${p.id}`}
                  >
                    <div className="col-span-4">
                      <div className="font-medium">{p.client_name || p.business_name || "—"}</div>
                      {p.industry && <div className="text-xs text-muted-foreground">{p.industry}</div>}
                    </div>
                    <div className="col-span-3">
                      <div className="text-xs text-muted-foreground">Rep</div>
                      <div className="text-sm">{p.submitted_by_name || "—"}</div>
                    </div>
                    <div className="col-span-2">
                      {statusMeta[p.status] ? (
                        <Badge className={statusMeta[p.status].className}>{statusMeta[p.status].label}</Badge>
                      ) : (
                        <Badge variant="outline">{p.status}</Badge>
                      )}
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </div>
                    <div className="col-span-1 text-right">
                      <Button asChild variant="ghost" size="sm" data-testid={`open-agency-proposal-${p.id}`}>
                        <Link to={`/proposals?open=${p.id}`}>Open</Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Clients grouped by Rep */}
      <Card className="border" data-testid="agency-clients-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-outfit flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Clients ({filteredClients.length}<span className="text-xs font-normal text-muted-foreground ml-1">of {clients.length}</span>)
            </CardTitle>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Live / Archived / All toggle */}
              <div className="inline-flex rounded-md border overflow-hidden" data-testid="client-filter-tabs">
                {[
                  { key: "live", label: `Live (${liveCount})` },
                  { key: "archived", label: `Archived (${archivedCount})` },
                  { key: "all", label: `All (${clients.length})` },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setClientFilter(tab.key)}
                    className={`px-3 py-1 text-xs font-medium transition ${clientFilter === tab.key ? "bg-primary text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}
                    data-testid={`client-filter-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={exportClientsCsv}
                data-testid="agency-export-clients-btn"
              >
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
              {isSuperAdmin && (
                <>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setAddClientOpen(true)}
                    data-testid="agency-add-client-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-1" /> Add Client
                  </Button>
                  <Link to="/admin/bulk-import">
                    <Button size="sm" variant="outline" data-testid="agency-bulk-import-btn">
                      <Upload className="h-4 w-4 mr-1" /> Bulk Import
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No clients linked to this agency yet.
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No {clientFilter === "archived" ? "archived" : "live"} clients in this view.
            </div>
          ) : (
            <div className="space-y-6">
              {(() => {
                // Group clients by rep, alphabetize clients within each rep
                const groups = new Map();
                filteredClients.forEach((c) => {
                  const key = c.rep_id || "_unassigned";
                  if (!groups.has(key)) {
                    groups.set(key, { rep_id: c.rep_id, rep_name: c.rep_name || "Unassigned", items: [] });
                  }
                  groups.get(key).items.push(c);
                });
                // Alphabetize clients within each rep
                for (const g of groups.values()) {
                  g.items.sort((a, b) => (a.company || a.name || "").toLowerCase().localeCompare((b.company || b.name || "").toLowerCase()));
                }
                // Alphabetize rep groups
                const sortedGroups = [...groups.values()].sort((a, b) => a.rep_name.toLowerCase().localeCompare(b.rep_name.toLowerCase()));
                return sortedGroups.map((g) => {
                  // Sum across ALL projects (not just primary) so MRR matches reality
                  const groupMRR = g.items.reduce(
                    (s, c) => s + (c.projects || []).reduce((ss, p) => ss + (Number(p.contract_value) || 0), 0),
                    0
                  );
                  const totalProjects = g.items.reduce((s, c) => s + (c.projects?.length || 0), 0);
                  return (
                    <div key={g.rep_id || "_unassigned"} data-testid={`rep-group-${g.rep_id || "unassigned"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {g.rep_id ? (
                            <Link
                              to={`/users/${g.rep_id}`}
                              className="font-medium text-primary hover:underline"
                              data-testid={`rep-group-link-${g.rep_id}`}
                            >
                              {g.rep_name}
                            </Link>
                          ) : (
                            <span className="font-medium">{g.rep_name}</span>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {g.items.length} client{g.items.length === 1 ? "" : "s"} · {totalProjects} order{totalProjects === 1 ? "" : "s"}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                          <DollarSign className="h-3.5 w-3.5" />
                          {formatMoney(groupMRR)} / mo
                        </div>
                      </div>
                      <div className="space-y-3">
                        {g.items.map((c) => {
                          const projects = c.projects && c.projects.length ? c.projects : (c.primary_project ? [c.primary_project] : []);
                          return (
                            <div key={c.id} className="rounded-md border" data-testid={`agency-client-row-${c.id}`}>
                              {/* Client header */}
                              <div className="px-4 py-2 bg-secondary/40 border-b flex items-center justify-between">
                                <div>
                                  <div className="font-semibold">{c.company || c.name}</div>
                                  {c.email && (
                                    <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                                  )}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {projects.length} order{projects.length === 1 ? "" : "s"}
                                </Badge>
                              </div>
                              {/* All orders for this client */}
                              <div className="divide-y">
                                {projects.length === 0 ? (
                                  <div className="px-4 py-3 text-xs text-muted-foreground italic">No orders yet</div>
                                ) : projects.map((p) => {
                                  const stage = p.lifecycle_stage;
                                  const stageMeta = lifecycleLabels[stage];
                                  return (
                                    <div key={p.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm">
                                      <div className="col-span-6 flex flex-wrap gap-1">
                                        {(p.sub_product_names || []).slice(0, 4).map((sp, i) => (
                                          <Badge key={i} variant="outline" className="text-xs">{sp}</Badge>
                                        ))}
                                        {(p.sub_product_names || []).length > 4 && (
                                          <Badge variant="outline" className="text-xs">+{p.sub_product_names.length - 4}</Badge>
                                        )}
                                        {(p.sub_product_names || []).length === 0 && (p.product_types || []).map((t) => (
                                          <Badge key={t} variant="outline" className="text-xs capitalize">{t.replace(/_/g, " ")}</Badge>
                                        ))}
                                      </div>
                                      <div className="col-span-2">
                                        {stageMeta ? (
                                          <Badge className={stageMeta.className}>{stageMeta.label}</Badge>
                                        ) : (
                                          <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                      </div>
                                      <div className="col-span-2 text-right tabular-nums">
                                        {formatMoney(p.contract_value)}
                                        {p.contract_value_type && (
                                          <span className={`ml-1 text-xs ${p.contract_value_type === "retail" ? "text-amber-700" : "text-blue-700"}`}>
                                            ({p.contract_value_type === "retail" ? "R" : "W"})
                                          </span>
                                        )}
                                      </div>
                                      <div className="col-span-2 text-right">
                                        <Button asChild variant="ghost" size="sm" data-testid={`agency-open-project-${p.id}`}>
                                          <Link to={`/orders/${p.id}`}>Open</Link>
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{selectedUser?.name}</strong> from {agency.name}. Their clients and projects will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Super-admin: add a client on behalf of this agency */}
      <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
        <DialogContent className="max-w-lg" data-testid="agency-add-client-dialog">
          <DialogHeader>
            <DialogTitle>Add client to {agency?.name}</DialogTitle>
            <DialogDescription>
              Creates a new client contact under this agency. You can assign a specific rep or let the portal auto-pick one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label>Company Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="add-client-company"
                value={newClient.company}
                onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                placeholder="e.g. Northside HVAC"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client Contact Name</Label>
                <Input
                  data-testid="add-client-contact"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <Label>Client Email <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="add-client-email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="jane@company.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Website</Label>
                <Input
                  data-testid="add-client-website"
                  value={newClient.website}
                  onChange={(e) => setNewClient({ ...newClient, website: e.target.value })}
                  placeholder="https://…"
                />
              </div>
              <div>
                <Label>Industry</Label>
                <Input
                  data-testid="add-client-industry"
                  value={newClient.industry}
                  onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                  placeholder="Roofing, Legal, HVAC…"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input
                  data-testid="add-client-phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Assign to Rep</Label>
                <Select
                  value={newClient.rep_id || "auto"}
                  onValueChange={(v) => setNewClient({ ...newClient, rep_id: v === "auto" ? "" : v })}
                >
                  <SelectTrigger data-testid="add-client-rep-select">
                    <SelectValue placeholder="Auto-pick" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-pick (first manager/rep)</SelectItem>
                    {agencyReps.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} · {u.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setAddClientOpen(false)} disabled={addingClient}>
              Cancel
            </Button>
            <Button
              onClick={handleAddClient}
              disabled={addingClient}
              data-testid="agency-add-client-save"
            >
              {addingClient ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Client
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// API_BASE imported from ../lib/api (smart-resolves to window.location.origin in
// production so logo <img> src URLs work on the same domain the user logged in on).

function AgencyBrandingCard({ agency, onChange }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [phone, setPhone] = useState(agency.voice_demo_phone || "");
  // Cache-buster so the logo refreshes after upload without a hard reload
  const [v, setV] = useState(Date.now());

  useEffect(() => { setPhone(agency.voice_demo_phone || ""); }, [agency.voice_demo_phone]);

  const canEdit = user?.role === "super_admin" || (user?.role === "manager" && user?.agency_id === agency.id);
  const logoSrc = agency.logo_url ? `${API_BASE}${agency.logo_url}?v=${v}` : null;

  const handleSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Logo must be an image (PNG / JPG / SVG)");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Logo must be < 5 MB");
      return;
    }
    setUploading(true);
    try {
      await uploadAgencyLogo(agency.id, f);
      toast.success("Logo uploaded");
      setV(Date.now());
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await updateAgencyBranding(agency.id, { voice_demo_phone: phone.trim() });
      toast.success("Voice demo phone saved");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <Card className="border" data-testid="agency-branding-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Branding
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo */}
        <div className="flex items-start gap-6 flex-wrap">
          <div className="shrink-0">
            <div className="w-44 h-28 bg-zinc-50 border border-dashed border-zinc-300 rounded-md flex items-center justify-center overflow-hidden">
              {logoSrc ? (
                <img src={logoSrc} alt={`${agency.name} logo`} className="max-w-full max-h-full object-contain" data-testid="agency-logo-preview" />
              ) : (
                <div className="text-center text-zinc-400">
                  <ImageIcon className="h-8 w-8 mx-auto opacity-50" />
                  <div className="text-xs mt-1">No logo yet</div>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-[260px]">
            <h4 className="font-semibold text-sm">Agency logo</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Used on the cover slide and the rep&apos;s contact page of every branded deck.
              PNG or JPG, max 5 MB. A transparent PNG looks best.
            </p>
            {canEdit && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileRef}
                  onChange={handleSelect}
                  data-testid="agency-logo-file-input"
                />
                <Button
                  className="mt-3"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="agency-logo-upload-btn"
                >
                  {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4 mr-2" /> {agency.logo_url ? "Replace logo" : "Upload logo"}</>}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Voice demo phone */}
        <div className="border-t pt-5">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <Phone className="h-4 w-4 text-rose-600" />
            AI Voice Agent Demo Phone
          </h4>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Each partner agency has a dedicated demo line. We auto-inject this number on
            slides <b>4, 6 &amp; 14</b> of the ProspectMax branded deck so prospects can call it to hear
            the AI voice agent live. Format: <code>(800) 555-0100</code>.
          </p>
          <div className="flex gap-2 mt-3 max-w-md">
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(800) 555-0100"
              disabled={!canEdit}
              data-testid="agency-voice-phone-input"
            />
            {canEdit && (
              <Button onClick={savePhone} disabled={savingPhone || phone === (agency.voice_demo_phone || "")} data-testid="agency-voice-phone-save">
                {savingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RepProfileEditor({ rep, onSaved }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(rep.title || "");
  const [phone, setPhone] = useState(rep.phone || "");
  const [calendarUrl, setCalendarUrl] = useState(rep.calendar_url || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(rep.title || "");
      setPhone(rep.phone || "");
      setCalendarUrl(rep.calendar_url || "");
    }
  }, [open, rep.title, rep.phone, rep.calendar_url]);

  // Same RBAC the backend enforces — render nothing if the viewer can't edit
  const canEdit = user?.id === rep.id
    || user?.role === "super_admin"
    || (user?.role === "manager" && user?.agency_id === rep.agency_id);
  if (!canEdit) return null;

  const save = async () => {
    setSaving(true);
    try {
      await updateUserProfile(rep.id, {
        title: title.trim(),
        phone: phone.trim(),
        calendar_url: calendarUrl.trim(),
      });
      toast.success("Profile updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`edit-rep-profile-${rep.id}`} title="Edit title / phone / calendar link">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit profile — {rep.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Director of Partner Success" data-testid={`rep-title-input-${rep.id}`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-1234" data-testid={`rep-phone-input-${rep.id}`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Calendar booking link</Label>
            <Input
              value={calendarUrl}
              onChange={(e) => setCalendarUrl(e.target.value)}
              placeholder="https://calendly.com/your-handle/15min"
              data-testid={`rep-calendar-input-${rep.id}`}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Calendly, Cal.com, Google Calendar, HubSpot Meetings — anything with a public booking URL. We&apos;ll add a scannable QR code to your contact slide so prospects can book a call straight from the deck.
            </p>
          </div>
          <p className="text-[11px] text-muted-foreground border-t pt-2">
            These appear on the final &ldquo;Your Partner&rdquo; contact page of every branded audit deck this rep generates.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`save-rep-profile-${rep.id}`}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
